/**
 * Componente compartido para una fila de carpeta. NO es recursivo — la
 * jerarquía se renderiza como una lista plana en FolderBrowser, indentada
 * según el nivel. Esto evita un bug del parser de Babel con recursión JSX
 * en este código base.
 */
import { useState } from "react";
import {
  FolderSimple, FolderSimplePlus, CaretRight, CaretDown,
  PencilSimple, Trash,
} from "@phosphor-icons/react";

const SYSTEM_FOLDERS = new Set(["informe", "eksvidi", "videos", "fotos"]);
export const isSystem = (path) => SYSTEM_FOLDERS.has((path || "").toLowerCase());

export function countAt(items, path) {
  if (!path) return items.length;
  const prefix = `${path}/`;
  return items.filter((it) => {
    const f = it.folder || "";
    return f === path || f.startsWith(prefix);
  }).length;
}

export function NewFolderInput({ value, onChange, onConfirm, onCancel, level = 0 }) {
  return (
    <div
      className="flex items-center gap-1 my-1 px-2 py-1 bg-amber-50 rounded-md border border-amber-200"
      style={{ marginLeft: `${level * 12}px` }}
    >
      <FolderSimplePlus className="w-3.5 h-3.5 text-amber-700" weight="bold" />
      <input
        type="text"
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onConfirm();
          if (e.key === "Escape") onCancel();
        }}
        placeholder="Nombre…"
        data-testid="folder-new-input"
        className="flex-1 min-w-0 text-[12px] h-6 px-1 bg-white border border-amber-300 rounded outline-none"
      />
      <button
        type="button"
        onClick={onConfirm}
        data-testid="folder-new-confirm"
        className="text-[10.5px] font-semibold text-amber-900 hover:text-amber-700 px-1"
      >
        OK
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="text-[10.5px] text-muted hover:text-foreground px-1"
      >
        ✕
      </button>
    </div>
  );
}

/**
 * Fila individual de carpeta (no recursiva).
 * Recibe el `node` y su `level` ya calculado.
 */
export default function FolderRow({
  node, level, items, selected, expanded, isOpen, hasChildren,
  onToggle, onSelect, onRename, onDelete, onCreateChild, onDrop,
}) {
  const [dragOver, setDragOver] = useState(false);
  const isSelected = selected === node.path;
  const count = countAt(items, node.path);
  const sys = isSystem(node.path);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { setDragOver(false); onDrop(e, node.path); }}
      className={`group flex items-center gap-1 pr-1 rounded-md text-[12.5px] transition-colors ${
        isSelected ? "bg-brand/10 text-brand font-semibold" :
        dragOver ? "bg-amber-100 border border-amber-300" :
        "text-foreground hover:bg-background"
      }`}
      style={{ paddingLeft: `${level * 12 + 4}px` }}
      data-testid={`folder-row-${node.path}`}
    >
      {hasChildren ? (
        <button
          type="button"
          onClick={() => onToggle(node.path)}
          className="text-muted hover:text-foreground w-4 h-5 inline-flex items-center justify-center"
        >
          {isOpen
            ? <CaretDown className="w-2.5 h-2.5" weight="bold" />
            : <CaretRight className="w-2.5 h-2.5" weight="bold" />}
        </button>
      ) : (
        <span className="w-4" />
      )}
      <button
        type="button"
        onClick={() => onSelect(node.path)}
        className="flex-1 min-w-0 inline-flex items-center gap-1.5 py-1 text-left"
      >
        <FolderSimple
          className="w-3.5 h-3.5 flex-shrink-0"
          weight={isSelected ? "fill" : "regular"}
        />
        <span className="truncate">{node.name}</span>
        {count > 0 && (
          <span className="text-[10px] text-muted tabular-nums">{count}</span>
        )}
      </button>
      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => onCreateChild(node.path)}
          data-testid={`folder-new-child-${node.path}`}
          title="Crear subcarpeta"
          className="text-muted hover:text-foreground p-0.5"
        >
          <FolderSimplePlus className="w-3 h-3" weight="bold" />
        </button>
        {!sys && (
          <>
            <button
              type="button"
              onClick={() => onRename(node.path)}
              data-testid={`folder-rename-${node.path}`}
              title="Renombrar"
              className="text-muted hover:text-foreground p-0.5"
            >
              <PencilSimple className="w-3 h-3" weight="bold" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(node.path)}
              data-testid={`folder-delete-${node.path}`}
              title="Eliminar"
              className="text-muted hover:text-rose-700 p-0.5"
            >
              <Trash className="w-3 h-3" weight="bold" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
